import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiAxios from "helpers/apiAxios";
import { handleAsyncThunkError } from "redux/reduxHelpers";

const baseUrl = (projectId) => `/crm/projects/${projectId}/stage_discussions`;

// ── Thunks ────────────────────────────────────────────────────────────────────

export const getStageDiscussions = createAsyncThunk(
  "StageDiscussion/getStageDiscussions",
  async ({ projectId, stage, page = 1, perPage = 10 }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(baseUrl(projectId), {
        params: { stage, page, per_page: perPage },
      });
      return { data: response.data.data, meta: response.data.meta, stage };
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to load discussions"));
    }
  }
);

export const createStageDiscussion = createAsyncThunk(
  "StageDiscussion/createStageDiscussion",
  async ({ projectId, stage, body, files = [] }, { rejectWithValue }) => {
    try {
      const fd = new FormData();
      fd.append("stage", stage);
      fd.append("body", body);
      files.forEach(f => fd.append("attachments[]", f));

      const response = await apiAxios.post(baseUrl(projectId), fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return { data: response.data.data, stage };
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to create discussion"));
    }
  }
);

export const replyToStageDiscussion = createAsyncThunk(
  "StageDiscussion/replyToStageDiscussion",
  async ({ projectId, discussionId, body, files = [] }, { rejectWithValue }) => {
    try {
      const fd = new FormData();
      fd.append("body", body);
      files.forEach(f => fd.append("attachments[]", f));

      const response = await apiAxios.post(`${baseUrl(projectId)}/${discussionId}/reply`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return { data: response.data.data, discussionId };
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to post reply"));
    }
  }
);

export const deleteStageDiscussion = createAsyncThunk(
  "StageDiscussion/deleteStageDiscussion",
  async ({ projectId, discussionId }, { rejectWithValue }) => {
    try {
      await apiAxios.delete(`${baseUrl(projectId)}/${discussionId}`);
      return { discussionId };
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to delete discussion"));
    }
  }
);

export const getDiscussionAttachmentEditorConfig = createAsyncThunk(
  "StageDiscussion/getAttachmentEditorConfig",
  async ({ projectId, blobId }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(
        `${baseUrl(projectId)}/attachment_editor_config`,
        { params: { blob_id: blobId } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to load attachment config"));
    }
  }
);

// ── Slice ─────────────────────────────────────────────────────────────────────

const initialState = {
  byStage:     {},   // { quotation: [...], negotiation: [...], enter_job: [...] }
  metaByStage: {},   // { <stage>: { total_rows, current_page, per_page, total_pages, ... } }
  loading:     false,
  sending:     false,
  error:       null,
};

const stageDiscussionSlice = createSlice({
  name: "crm_stage_discussion",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // getStageDiscussions
    builder.addCase(getStageDiscussions.pending, (state) => {
      state.loading = true;
      state.error   = null;
    });
    builder.addCase(getStageDiscussions.fulfilled, (state, { payload }) => {
      state.loading                    = false;
      state.byStage[payload.stage]     = payload.data;
      state.metaByStage[payload.stage] = payload.meta || null;
    });
    builder.addCase(getStageDiscussions.rejected, (state, { payload }) => {
      state.loading = false;
      state.error   = payload;
    });

    // createStageDiscussion
    builder.addCase(createStageDiscussion.pending, (state) => { state.sending = true; });
    builder.addCase(createStageDiscussion.fulfilled, (state, { payload }) => {
      state.sending = false;
      const list = state.byStage[payload.stage] || [];
      state.byStage[payload.stage] = [payload.data, ...list];
      // Keep the 'all' tab in sync if it has been loaded
      if (state.byStage['all']) {
        state.byStage['all'] = [payload.data, ...state.byStage['all']];
      }
    });
    builder.addCase(createStageDiscussion.rejected, (state, { payload }) => {
      state.sending = false;
      state.error   = payload;
    });

    // replyToStageDiscussion
    builder.addCase(replyToStageDiscussion.pending, (state) => { state.sending = true; });
    builder.addCase(replyToStageDiscussion.fulfilled, (state, { payload }) => {
      state.sending = false;
      Object.keys(state.byStage).forEach(stage => {
        state.byStage[stage] = state.byStage[stage].map(d => {
          if (d.id !== payload.discussionId) return d;
          return { ...d, replies: [...(d.replies || []), payload.data] };
        });
      });
    });
    builder.addCase(replyToStageDiscussion.rejected, (state, { payload }) => {
      state.sending = false;
      state.error   = payload;
    });

    // deleteStageDiscussion
    builder.addCase(deleteStageDiscussion.fulfilled, (state, { payload }) => {
      Object.keys(state.byStage).forEach(stage => {
        state.byStage[stage] = state.byStage[stage].filter(d => d.id !== payload.discussionId);
      });
    });
  },
});

export default stageDiscussionSlice.reducer;
